import React, { useContext } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getEnv } from '@cidade360/core-lib/env';
import {
  SettingsKeys,
  getLocalConfig,
  setLocalConfig,
  removeLocalConfig,
} from '@cidade360/ui-lib/hooks/localConfig';
import { useSessionStore } from '@cidade360/core-lib/repo/session';
import { UserPermissions } from '@cidade360/core-lib/validation';
import i18n from 'i18next';

export type HeaderTitleContextValue = {
  content: React.ReactNode | null;
  setContent: (c: React.ReactNode | null) => void;
};
export const HeaderTitleContext = React.createContext<HeaderTitleContextValue>({
  content: null,
  setContent: () => {},
});

export function HeaderTitle({
  children,
}: {
  children: React.ReactNode | null;
}) {
  const { setContent } = React.useContext(HeaderTitleContext);
  React.useEffect(() => {
    setContent(children);
  }, [children, setContent]);
  return null;
}

export function HeaderIconTitle({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactElement<any> | null;
}) {
  const theme = useTheme();
  const Icon: any = icon;
  return (
    <ListItem
      component="div"
      css={{
        width: 'auto',
        color: theme.palette.grey[600],
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      }}
    >
      <ListItemIcon css={{ minWidth: 32 }}>{Icon}</ListItemIcon>
      <ListItemText primary={title} />
    </ListItem>
  );
}

export function HeaderTitleRenderer() {
  const { content } = React.useContext(HeaderTitleContext);

  return <>{content}</>;
}

export function HeaderTitleContextContainer(props: {
  children?: React.ReactNode;
}) {
  const [headerTitleContent, setHeaderTitleContent] =
    React.useState<React.ReactNode | null>(null);

  return (
    <HeaderTitleContext.Provider
      value={{
        content: headerTitleContent,
        setContent: setHeaderTitleContent,
      }}
    >
      {props?.children}
    </HeaderTitleContext.Provider>
  );
}

export enum ChatQueryPrams {
  openChatWithUserID = 'openChatWith',
}
export enum ChatVariant {
  double = 'double',
  single = 'single',
  wrap = 'wrap',
}
export enum ChatMessageSize {
  small = 'small',
  large = 'large',
}
export enum ChatPosition {
  right = 'right',
  left = 'left',
}

export type ChatData = {
  userID: string | null;
  appoID?: string;
  apscID?: string;
  megrID?: string;
  variant?: ChatVariant;
  messageSize?: ChatMessageSize;
  chatPosition?: ChatPosition;
  classes?: {
    chatWindow?: string;
  };
  onPressActions?: () => void;
};

export type AppointmentData = {
  appoID: string;
  patiID: string;
  section?: 'doctorNote';
};

export enum Module {
  survey = 'survey',
  security = 'security',
  operations = 'operations',
  planning = 'planning',
  suppliers = 'suppliers',
}

export function getModuleLabel(mod: Module): string {
  return (
    {
      [Module.survey]: i18n.t('navigation:surveys'),
      [Module.security]: i18n.t('navigation:settings'),
      [Module.operations]: i18n.t('navigation:operations'),
      [Module.planning]: i18n.t('navigation:planningModule'),
      [Module.suppliers]: i18n.t('navigation.suppliers'),
    }[mod] ?? mod
  );
}

export type ModulesEnabled = {
  [Module.survey]: boolean;
  [Module.security]: boolean;
  [Module.operations]: boolean;
  [Module.planning]: boolean;
  [Module.suppliers]: boolean;
};

export type UIContextValue = {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  setAsUserID: (userID?: string) => void;
  modules: ModulesEnabled;
  asUserID?: string;
};

export const UIContext = React.createContext<UIContextValue>({
  sidebarOpen: true,
  setSidebarOpen: () => {},
  setAsUserID: () => {},
  modules: {
    [Module.survey]: true,
    [Module.security]: true,
    [Module.operations]: true,
    [Module.planning]: true,
    [Module.suppliers]: true,
  },
});

export function UIContextContainer(props: { children?: React.ReactNode }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [asUserID, setAsUserID] = React.useState<string>();
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(isLg);
  const { roles, user } = useSessionStore();
  const isAdmin = roles.includes(UserPermissions.admin);
  const currentUserID = React.useMemo(() => user?.userID, [user]);

  const modules = React.useMemo(() => {
    const env = getEnv();
    console.log('!!! env', env);
    return {
      survey: env.DISABLE_SURVEY_MODULE !== 'true',
      security: env.DISABLE_SECURITY_MODULE !== 'true',
      operations: env.DISABLE_OPERATIONS_MODULE !== 'true',
      planning: env.DISABLE_PLANNING_MODULE !== 'true',
      suppliers: env.DISABLE_SUPPLIERS_MODULE !== 'true',
    };
  }, []);

  const setStateAsUserID = React.useCallback(
    (userID?: string) => {
      let id = userID;
      if (!isAdmin && userID && userID !== currentUserID) {
        id = currentUserID;
      }
      setAsUserID(id);
      if (id) {
        setLocalConfig(SettingsKeys.AsUserID, id);
      } else {
        removeLocalConfig(SettingsKeys.AsUserID);
      }
    },
    [isAdmin, currentUserID]
  );

  React.useEffect(() => {
    if (!isLg) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isLg]);

  React.useEffect(() => {
    let userID = getLocalConfig(SettingsKeys.AsUserID)?.value;
    if (userID) {
      if (!isAdmin && userID !== currentUserID) {
        userID = currentUserID;
      }
      setAsUserID(userID);
    }
  }, [currentUserID]);

  return (
    <UIContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        modules,
        asUserID,
        setAsUserID: setStateAsUserID,
      }}
    >
      {props?.children}
    </UIContext.Provider>
  );
}

export const useUIContext = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIContextContainer');
  }
  return context;
};
